/*
 * IndexedDBStore — Wrapper um die IndexedDB-API
 *
 * Verwaltet vier Object Stores:
 * - projects:     Projekt-Metadaten (ID, Titel, Autor, Cursor-State, folderId, sizeBytes, etc.)
 * - paragraphs:   Absatz-Inhalte (ID, Projekt-ID, Daten, Version)
 * - folders:      Folder-Hierarchie (ID, userId, parentFolderId, name, version, deletedAt)
 * - dictionaries: Hunspell-Woerterbuecher fuer Spellcheck (lang, aff, dic, etag)
 *
 * Alle Operationen sind asynchron und verwenden Promises.
 * Batch-Writes nutzen eine einzelne Transaction für Atomizität.
 */

const DB_NAME = 'carbisdale'
const DB_VERSION = 4
const STORE_PROJECTS = 'projects'
const STORE_PARAGRAPHS = 'paragraphs'
const STORE_DICTIONARIES = 'dictionaries'
const STORE_FOLDERS = 'folders'

export class IndexedDBStore {
    constructor(dbName = DB_NAME, version = DB_VERSION) {
        this._dbName = dbName
        this._version = version
        this._db = null
    }

    /**
     * Öffnet die Datenbank und legt Object Stores an falls nötig.
     */
    async open() {
        if (this._db) return this._db

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this._dbName, this._version)

            request.onupgradeneeded = (event) => {
                const db = event.target.result
                const oldVersion = event.oldVersion

                // Projects Store
                if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
                    const projectStore = db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' })
                    projectStore.createIndex('updatedAt', 'updatedAt', { unique: false })
                    projectStore.createIndex('deletedAt', 'deletedAt', { unique: false })
                    projectStore.createIndex('userId', 'userId', { unique: false })
                }

                // Paragraphs Store
                if (!db.objectStoreNames.contains(STORE_PARAGRAPHS)) {
                    const paragraphStore = db.createObjectStore(STORE_PARAGRAPHS, { keyPath: 'id' })
                    paragraphStore.createIndex('projectId', 'projectId', { unique: false })
                }

                // Migration v1 → v2: userId-Index auf bestehenden Projects-Store
                if (oldVersion < 2 && db.objectStoreNames.contains(STORE_PROJECTS)) {
                    const projectStore = event.target.transaction.objectStore(STORE_PROJECTS)
                    if (!projectStore.indexNames.contains('userId')) {
                        projectStore.createIndex('userId', 'userId', { unique: false })
                    }
                }

                // Migration v2 → v3: Dictionaries-Store fuer Spellcheck
                // (Hunspell aff/dic Cache, ETag-basierte Updates).
                if (oldVersion < 3 && !db.objectStoreNames.contains(STORE_DICTIONARIES)) {
                    db.createObjectStore(STORE_DICTIONARIES, { keyPath: 'lang' })
                }

                // Migration v3 → v4: Folders-Store fuer Project-Dashboard Phase 4.
                // Felder: id, userId, parentFolderId (null=Root), name, version,
                //         createdAt, updatedAt, deletedAt (Soft-Delete reserviert).
                // Bestehende Projekte bleiben implizit auf folderId === undefined,
                // was semantisch == null == Root ist. sizeBytes wird beim ersten
                // Save automatisch geschrieben; Backfill-Task fuellt rueckwirkend.
                if (oldVersion < 4 && !db.objectStoreNames.contains(STORE_FOLDERS)) {
                    const folderStore = db.createObjectStore(STORE_FOLDERS, { keyPath: 'id' })
                    folderStore.createIndex('userId', 'userId', { unique: false })
                    folderStore.createIndex('parentFolderId', 'parentFolderId', { unique: false })
                    folderStore.createIndex('deletedAt', 'deletedAt', { unique: false })
                }
            }

            request.onsuccess = (event) => {
                this._db = event.target.result
                resolve(this._db)
            }

            request.onerror = (event) => {
                reject(new Error(`IndexedDB Fehler: ${event.target.error}`))
            }
        })
    }

    /**
     * Schließt die Datenbank-Verbindung.
     */
    close() {
        if (this._db) {
            this._db.close()
            this._db = null
        }
    }

    // ─────────────────────────────────────────────
    // Projects
    // ─────────────────────────────────────────────

    /**
     * Lädt ein einzelnes Projekt.
     * @param {string} id - Projekt-UUID
     * @returns {Promise<Object|undefined>}
     */
    async getProject(id) {
        return this._get(STORE_PROJECTS, id)
    }

    /**
     * Lädt alle aktiven Projekte eines Users (ohne gelöschte).
     * @param {string} [userId] - User-ID zum Filtern (optional fuer Abwaertskompatibilitaet)
     * @returns {Promise<Object[]>}
     */
    async getAllProjects(userId) {
        const all = await this._getAll(STORE_PROJECTS)
        return all.filter(p => !p.deletedAt && (!userId || p.userId === userId))
    }

    /**
     * Lädt alle gelöschten Projekte eines Users (Papierkorb).
     * @param {string} [userId] - User-ID zum Filtern (optional fuer Abwaertskompatibilitaet)
     * @returns {Promise<Object[]>}
     */
    async getDeletedProjects(userId) {
        const all = await this._getAll(STORE_PROJECTS)
        return all.filter(p => p.deletedAt && (!userId || p.userId === userId))
    }

    /**
     * Speichert oder aktualisiert ein Projekt.
     * @param {Object} project - Projekt-Objekt mit id-Feld
     */
    async putProject(project) {
        return this._put(STORE_PROJECTS, project)
    }

    /**
     * Löscht ein Projekt permanent (mit allen Paragraphen).
     * @param {string} id - Projekt-UUID
     */
    async deleteProject(id) {
        const db = await this._ensureOpen()
        const tx = db.transaction([STORE_PROJECTS, STORE_PARAGRAPHS], 'readwrite')

        return new Promise((resolve, reject) => {
            // Zuerst alle Paragraphen des Projekts löschen
            const paragraphStore = tx.objectStore(STORE_PARAGRAPHS)
            const index = paragraphStore.index('projectId')
            const request = index.openCursor(IDBKeyRange.only(id))

            request.onsuccess = (event) => {
                const cursor = event.target.result
                if (cursor) {
                    cursor.delete()
                    cursor.continue()
                }
            }

            // Dann das Projekt selbst löschen
            tx.objectStore(STORE_PROJECTS).delete(id)

            tx.oncomplete = () => resolve()
            tx.onerror = (event) => reject(new Error(`Löschen fehlgeschlagen: ${event.target.error}`))
        })
    }

    // ─────────────────────────────────────────────
    // Paragraphs
    // ─────────────────────────────────────────────

    /**
     * Lädt alle Paragraphen eines Projekts.
     * @param {string} projectId - Projekt-UUID
     * @returns {Promise<Object[]>}
     */
    async getParagraphsByProject(projectId) {
        const db = await this._ensureOpen()
        const tx = db.transaction(STORE_PARAGRAPHS, 'readonly')
        const index = tx.objectStore(STORE_PARAGRAPHS).index('projectId')

        return new Promise((resolve, reject) => {
            const request = index.getAll(IDBKeyRange.only(projectId))
            request.onsuccess = () => resolve(request.result)
            request.onerror = (event) => reject(new Error(`Lesen fehlgeschlagen: ${event.target.error}`))
        })
    }

    /**
     * Lädt mehrere Paragraphen per ID (Batch-Punkt-Read).
     * Eine einzige readonly-Transaktion, keine Index-Scans.
     * @param {string[]} ids - Array von Paragraph-UUIDs
     * @returns {Promise<Object[]>} Gefundene Paragraphen (fehlende IDs werden übersprungen)
     */
    async getParagraphsByIds(ids) {
        if (!ids || ids.length === 0) return []

        const db = await this._ensureOpen()
        const tx = db.transaction(STORE_PARAGRAPHS, 'readonly')
        const store = tx.objectStore(STORE_PARAGRAPHS)

        return new Promise((resolve, reject) => {
            const results = []
            for (const id of ids) {
                const request = store.get(id)
                request.onsuccess = () => {
                    if (request.result !== undefined) results.push(request.result)
                }
            }
            tx.oncomplete = () => resolve(results)
            tx.onerror = (event) => reject(new Error(`Batch-Read fehlgeschlagen: ${event.target.error}`))
        })
    }

    /**
     * Liefert eine kompakte Map<id, version> aller Paragraphen eines Projekts.
     *
     * Nutzt intern `index.getAll()` statt eines Cursors: structured clone
     * passiert in beiden Faellen fuer alle Records, aber Cursor hat pro
     * Record Event-Dispatch-Overhead, waehrend getAll alles in einer einzigen
     * Operation batcht. Messbar etwa 2x schneller.
     *
     * @param {string} projectId - Projekt-UUID
     * @returns {Promise<Map<string, number>>}
     */
    async getParagraphIdVersionMap(projectId) {
        const all = await this.getParagraphsByProject(projectId)
        const map = new Map()
        for (const p of all) {
            map.set(p.id, p.version || 1)
        }
        return map
    }

    /**
     * Speichert oder aktualisiert einen einzelnen Paragraphen.
     * @param {Object} paragraph - Paragraph-Objekt mit id-Feld
     */
    async putParagraph(paragraph) {
        return this._put(STORE_PARAGRAPHS, paragraph)
    }

    /**
     * Speichert mehrere Paragraphen in einer Transaction (atomar).
     * @param {Object[]} paragraphs - Array von Paragraph-Objekten
     */
    async putParagraphs(paragraphs) {
        if (paragraphs.length === 0) return

        const db = await this._ensureOpen()
        const tx = db.transaction(STORE_PARAGRAPHS, 'readwrite')
        const store = tx.objectStore(STORE_PARAGRAPHS)

        return new Promise((resolve, reject) => {
            for (const p of paragraphs) {
                store.put(p)
            }
            tx.oncomplete = () => resolve()
            tx.onerror = (event) => reject(new Error(`Batch-Write fehlgeschlagen: ${event.target.error}`))
        })
    }

    /**
     * Löscht einen einzelnen Paragraphen.
     * @param {string} id - Paragraph-UUID
     */
    async deleteParagraph(id) {
        return this._delete(STORE_PARAGRAPHS, id)
    }

    /**
     * Löscht alle Paragraphen eines Projekts.
     * @param {string} projectId - Projekt-UUID
     */
    async deleteParagraphsByProject(projectId) {
        const db = await this._ensureOpen()
        const tx = db.transaction(STORE_PARAGRAPHS, 'readwrite')
        const index = tx.objectStore(STORE_PARAGRAPHS).index('projectId')

        return new Promise((resolve, reject) => {
            const request = index.openCursor(IDBKeyRange.only(projectId))
            request.onsuccess = (event) => {
                const cursor = event.target.result
                if (cursor) {
                    cursor.delete()
                    cursor.continue()
                }
            }
            tx.oncomplete = () => resolve()
            tx.onerror = (event) => reject(new Error(`Löschen fehlgeschlagen: ${event.target.error}`))
        })
    }

    // ─────────────────────────────────────────────
    // Folders
    // ─────────────────────────────────────────────

    /**
     * Laedt einen einzelnen Folder.
     * @param {string} id - Folder-UUID
     * @returns {Promise<Object|undefined>}
     */
    async getFolder(id) {
        return this._get(STORE_FOLDERS, id)
    }

    /**
     * Laedt alle aktiven Folder eines Users (ohne soft-geloeschte).
     * Soft-Delete ist reserviert — heute werden Folder hart geloescht
     * (siehe Spec §5.2), aber der Filter haelt die API zukunftsoffen.
     * @param {string} [userId]
     * @returns {Promise<Object[]>}
     */
    async getAllFolders(userId) {
        const all = await this._getAll(STORE_FOLDERS)
        return all.filter(f => !f.deletedAt && (!userId || f.userId === userId))
    }

    /**
     * Speichert oder aktualisiert einen Folder.
     * @param {Object} folder - Folder-Objekt mit id-Feld
     */
    async putFolder(folder) {
        return this._put(STORE_FOLDERS, folder)
    }

    /**
     * Loescht einen einzelnen Folder hart (kein Cascade auf Sub-Folder oder
     * Projekte — der Aufrufer ist verantwortlich, das vorher selbst zu
     * regeln, analog zur Server-Logik).
     * @param {string} id - Folder-UUID
     */
    async deleteFolder(id) {
        return this._delete(STORE_FOLDERS, id)
    }

    // ─────────────────────────────────────────────
    // Dictionaries (Spellcheck)
    // ─────────────────────────────────────────────

    /**
     * Liest ein gecachtes Woerterbuch.
     * @param {string} lang - Sprach-Code, z.B. 'de'
     * @returns {Promise<{lang:string, aff:ArrayBuffer, dic:ArrayBuffer, etag?:string, fetchedAt?:number} | undefined>}
     */
    async getDictionary(lang) {
        return this._get(STORE_DICTIONARIES, lang)
    }

    /**
     * Speichert ein Woerterbuch im Cache.
     * @param {Object} dict - { lang, aff: ArrayBuffer, dic: ArrayBuffer, etag?, fetchedAt? }
     */
    async putDictionary(dict) {
        return this._put(STORE_DICTIONARIES, dict)
    }

    /**
     * Loescht ein gecachtes Woerterbuch.
     * @param {string} lang - Sprach-Code
     */
    async deleteDictionary(lang) {
        return this._delete(STORE_DICTIONARIES, lang)
    }

    // ─────────────────────────────────────────────
    // Interne Hilfsmethoden
    // ─────────────────────────────────────────────

    async _ensureOpen() {
        if (!this._db) await this.open()
        return this._db
    }

    async _get(storeName, id) {
        const db = await this._ensureOpen()
        const tx = db.transaction(storeName, 'readonly')
        const store = tx.objectStore(storeName)

        return new Promise((resolve, reject) => {
            const request = store.get(id)
            request.onsuccess = () => resolve(request.result)
            request.onerror = (event) => reject(new Error(`Lesen fehlgeschlagen: ${event.target.error}`))
        })
    }

    async _getAll(storeName) {
        const db = await this._ensureOpen()
        const tx = db.transaction(storeName, 'readonly')
        const store = tx.objectStore(storeName)

        return new Promise((resolve, reject) => {
            const request = store.getAll()
            request.onsuccess = () => resolve(request.result)
            request.onerror = (event) => reject(new Error(`Lesen fehlgeschlagen: ${event.target.error}`))
        })
    }

    async _put(storeName, value) {
        const db = await this._ensureOpen()
        const tx = db.transaction(storeName, 'readwrite')
        const store = tx.objectStore(storeName)

        return new Promise((resolve, reject) => {
            store.put(value)
            tx.oncomplete = () => resolve()
            tx.onerror = (event) => reject(new Error(`Schreiben fehlgeschlagen: ${event.target.error}`))
        })
    }

    async _delete(storeName, id) {
        const db = await this._ensureOpen()
        const tx = db.transaction(storeName, 'readwrite')
        const store = tx.objectStore(storeName)

        return new Promise((resolve, reject) => {
            store.delete(id)
            tx.oncomplete = () => resolve()
            tx.onerror = (event) => reject(new Error(`Löschen fehlgeschlagen: ${event.target.error}`))
        })
    }
}
